<template>
  <div class="vf-m-new-cart-footer">
    <div class="vf-m-new-cart-footer__total">
      <div class="vf-m-new-cart-footer__subtotal">
        <p>{{ $t("elements.store.cart.totalText") }}</p>
        <p>{{ overallTotal }}</p>
      </div>
      <div class="vf-m-new-cart-footer__shipping-type">
        <p>{{ $t("elements.store.cart.shippingType") }}</p>
        <span>Free</span>
      </div>
    </div>
    <div
      class="order-gift-option"
      :class="{ 'order-gift-option--selected': isGiftOrder }"
    >
      <input
        :id="giftOrderInputId"
        type="checkbox"
        :checked="isGiftOrder"
        @change="handleGiftOrderChange"
      />
      <label :for="giftOrderInputId">
        <svg
          class="order-gift-option__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M4 10h16v11H4zM3 6h18v4H3zM12 6v15" />
          <path
            d="M12 6H8.5A2.5 2.5 0 1 1 11 3.5L12 6Zm0 0h3.5A2.5 2.5 0 1 0 13 3.5L12 6Z"
          />
        </svg>
        <span class="order-gift-option__copy"
          ><span>Make it a gift</span
          ><small>Thoughtfully wrapped, ready to give.</small></span
        >
      </label>
    </div>
    <button
      class="w-full custom-button primary-btn"
      @click="goTo"
      :disabled="cart.products.length === 0"
    >
      {{ $t("elements.store.cart.checkoutButton") }}
    </button>
  </div>
</template>
<script lang="ts" setup>
import { computed, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  cartToGa4Items,
  ga4ItemsValue,
  pushGa4EcommerceEvent,
} from "../../../utils/ga4Ecommerce";
import { rememberCheckoutReturnPath } from "../../../utils/checkoutReturn";
import { useGiftOrderPreference } from "../../../utils/giftOrder";

const props = defineProps({
  cart: {
    type: Object,
    default() {
      return null;
    },
  },
});

const router = useRouter();
const route = useRoute();
const store = useStore();
const giftOrderInputId = `shopping-bag-gift-order-${
  getCurrentInstance()?.uid ?? 0
}`;
const { isGiftOrder, setIsGiftOrder } = useGiftOrderPreference();
const handleGiftOrderChange = (event: Event) => {
  setIsGiftOrder((event.target as HTMLInputElement)?.checked === true);
};

const overallTotal = computed(() => {
  let total = "$";

  for (const product of props.cart.products) {
    const amount =
      Number(total.replace("$", "").replace(",", "")) +
      Number(product.total.replace("$", "").replace(",", ""));
    total = "$" + amount.toFixed(2);
  }

  return total.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
});

// Comment: Push GA4 begin_checkout and then navigate to checkout
const goTo = () => {
  try {
    const items = cartToGa4Items(props.cart);
    pushGa4EcommerceEvent("begin_checkout", {
      currency: "USD",
      value: ga4ItemsValue(items),
      items,
    });
  } catch {
    // Non-blocking: navigation proceeds even if tracking fails
  }
  // A same-route push is ignored by Vue Router, so the drawer used to remain
  // open over checkout. Close it explicitly and ask the mounted checkout to
  // recalculate after cart edits without discarding the shopper's form data.
  store.commit("vuefront/setSidebarCart", false);
  window.dispatchEvent(new CustomEvent("vf:shopping-bag-checkout"));

  if (route.path.replace(/\/$/, "") !== "/store/checkout") {
    rememberCheckoutReturnPath(route.fullPath);
    router.push("/store/checkout/");
  }
};
</script>
